// @@TECH_PACKS_v94@@ Public, read-only tech pack share page: /public/techpack/:token
// Standalone (no app shell, no theme.css) like PublicProduction. Data from the
// anonymous token-scoped API at /pub-api/techpacks/:token (NOT /public — that
// path is this page). One button downloads everything as a single .zip.
// @@TECH_PACKS_DISCHARGE_v95@@ discharge press settings + blank compatibility for the printer.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PlacementPreview, Swatch, fmtYmd, fmtBytes, num, blankText } from '../components/TechPackParts';

const C = { bg: '#f4f4f5', card: '#ffffff', line: '#e4e4e7', txt: '#141417', muted: '#6b6b72', dark: '#0a0a0b', green: '#31b44b' };
const S = {
  page: { minHeight: '100vh', background: C.bg, color: C.txt, fontFamily: "'Archivo', system-ui, -apple-system, sans-serif" },
  bar: { background: C.dark, borderBottom: `3px solid ${C.green}`, color: '#fff', padding: '14px 16px' },
  barIn: { maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' },
  wrap: { maxWidth: 1080, margin: '0 auto', padding: '20px 16px 60px' },
  card: { background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: 20, marginBottom: 16 },
  k: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: C.muted, fontWeight: 600, marginBottom: 4, display: 'block' },
  btn: { display: 'inline-flex', alignItems: 'center', height: 42, padding: '0 18px', borderRadius: 10, background: C.green, color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none', border: 0 },
  btn2: { display: 'inline-flex', alignItems: 'center', height: 42, padding: '0 16px', borderRadius: 10, background: '#fff', color: C.txt, fontWeight: 600, fontSize: 14, textDecoration: 'none', border: `1px solid ${C.line}` },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
};
const STATUS = { draft: 'Draft', review: 'In client review', approved: 'Approved', changes_requested: 'Changes requested' };
// @@TECH_PACK_LABELS_v112@@ optional 4x6 label pages for the printer (specs / packing list / preview).
const LABEL_KINDS = [['specs', 'Tech specs'], ['packing', 'Packing list'], ['preview', 'Preview']];

export default function PublicTechPack() {
  const { token } = useParams();
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState('');
  const [pick, setPick] = useState(LABEL_KINDS.map(([k]) => k)); // @@TECH_PACK_LABELS_v112@@
  useEffect(() => {
    fetch(`/pub-api/techpacks/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setPack(d.pack))
      .catch((s) => setErr(s === 404 ? 'This link is no longer active.' : 'Could not load this tech pack.'));
  }, [token]);
  useEffect(() => { if (pack) document.title = `${pack.code} — ${pack.title} · XYZ Merchandise`; }, [pack]);
  const base = `/pub-api/techpacks/${encodeURIComponent(token)}`;
  // @@TECH_PACK_THUMBS_v97@@ file(id) = original download; file(id, 'sm'|'md') = cached preview
  const file = (id, size) => `${base}/files/${id}${size ? '?thumb=' + size : ''}`;

  return (
    <div style={S.page}>
      <div style={S.bar}>
        <div style={S.barIn}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/brand/xyz-mark.png" alt="XYZ Merchandise" style={{ height: 30 }} />
            <div><div style={{ fontWeight: 800, letterSpacing: '.04em' }}>XYZ MERCHANDISE</div>
              <div style={{ fontSize: 11, color: '#8a8a90', letterSpacing: '.14em' }}>SCREEN PRINT TECH PACK</div></div>
          </div>
          {pack && <div style={{ ...S.mono, fontWeight: 700 }}>{pack.code} · Rev {pack.revision}</div>}
        </div>
      </div>
      <div style={S.wrap}>
        {err && <div style={S.card}>{err}</div>}
        {!pack && !err && <div style={S.card}>Loading…</div>}
        {pack && (
          <>
            <div style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div>
                  <h1 style={{ margin: '0 0 6px', fontSize: 26 }}>{pack.title}</h1>
                  <div style={{ color: C.muted }}>{[pack.client_name, pack.client_style_code ? 'Style # ' + pack.client_style_code : null, STATUS[pack.status]].filter(Boolean).join(' · ')}</div>
                  {pack.status === 'approved' && pack.decided_name && <div style={{ color: '#1f7a36', fontWeight: 600, marginTop: 6 }}>Approved by {pack.decided_name}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <a style={S.btn} href={`${base}/download.zip`}>Download all files (.zip)</a>
                  <a style={S.btn2} href={`${base}/pdf?inline=1`} target="_blank" rel="noreferrer">View PDF</a>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.line}`, fontSize: 14 }}>
                <span style={{ ...S.k, marginBottom: 0 }}>4×6 labels</span>
                {LABEL_KINDS.map(([k, t]) => (
                  <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32, cursor: 'pointer' }}>
                    <input type="checkbox" checked={pick.includes(k)} onChange={() => setPick(LABEL_KINDS.map(([x]) => x).filter((x) => (x === k ? !pick.includes(x) : pick.includes(x))))} /> {t}
                  </label>
                ))}
                {pick.length > 0 ? (
                  <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                    <a style={{ ...S.btn2, height: 34 }} href={`${base}/pdf?inline=1&labels=${pick.join(',')}`} target="_blank" rel="noreferrer">PDF + labels</a>
                    <a style={{ ...S.btn2, height: 34 }} href={`${base}/pdf?inline=1&only=1&labels=${pick.join(',')}`} target="_blank" rel="noreferrer">Labels only</a>
                  </span>
                ) : <span style={{ color: C.muted }}>Pick at least one label.</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginTop: 18 }}>
                {[['Blank', blankText(pack.blank)], ['Garment color', pack.blank.color], ['Fabric', pack.blank.fabric], ['Sizes', pack.sizes],
                  ['Quantity', pack.quantity != null ? Number(pack.quantity).toLocaleString() + ' pcs' : null], ['Due', pack.due_date ? fmtYmd(pack.due_date) : null],
                  ['Print method', pack.style_text], ['Default mesh', pack.mesh_default ? pack.mesh_default + ' mesh' : null]]
                  .filter(([, v]) => v).map(([k, v]) => <div key={k}><span style={S.k}>{k}</span>{v}</div>)}
              </div>
              {pack.uses_discharge && (
                <div style={{ marginTop: 16, background: pack.blank.dischargeable === 'no' ? '#fdecec' : '#fff7e6', border: `1px solid ${pack.blank.dischargeable === 'no' ? '#f3b4ae' : '#f0c970'}`, borderRadius: 10, padding: '12px 14px', lineHeight: 1.5 }}>
                  <span style={{ ...S.k, color: pack.blank.dischargeable === 'no' ? '#b42318' : '#8a5a00' }}>Discharge print</span>
                  {pack.discharge_text && <div>{pack.discharge_text}</div>}
                  {pack.discharge && pack.discharge.result_color && <div>Expected garment color after discharge: <strong>{pack.discharge.result_color}</strong></div>}
                  {pack.discharge && pack.discharge.notes && <div style={{ color: C.muted }}>{pack.discharge.notes}</div>}
                  {pack.discharge_warning && <div style={{ fontWeight: 700, color: pack.blank.dischargeable === 'no' ? '#b42318' : '#8a5a00', marginTop: 4 }}>{pack.discharge_warning}</div>}
                </div>
              )}
              {pack.notes && <div style={{ marginTop: 16 }}><span style={S.k}>Production notes</span><div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{pack.notes}</div></div>}
            </div>

            {pack.mockups.length > 0 && (
              <div style={S.card}>
                <span style={S.k}>Mockups</span>
                <div style={S.grid}>
                  {pack.mockups.map((m) => (
                    <a key={m.id} href={file(m.id)} style={{ textDecoration: 'none', color: C.txt }}>
                      <div style={{ background: C.bg, borderRadius: 10, aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        <img src={file(m.id, 'sm')} alt={m.filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{m.label || m.filename}</div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {pack.locations.map((l, i) => {
              const art = l.files.find((f) => f.is_image);
              return (
                <div key={l.id} style={S.card}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <span style={{ width: 30, height: 30, borderRadius: 15, background: C.dark, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{String(i + 1).padStart(2, '0')}</span>
                    <h2 style={{ margin: 0, fontSize: 19 }}>{l.name}</h2>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                    <div style={{ background: C.bg, borderRadius: 10, minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                      {art ? <img src={file(art.id, 'md')} alt={l.name} style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} /> : <span style={{ color: C.muted }}>See art files below</span>}
                    </div>
                    <div style={{ background: C.bg, borderRadius: 10, padding: 10 }}>
                      <PlacementPreview loc={l} artUrl={art ? file(art.id, 'md') : ''} height={260} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginTop: 16 }}>
                    <div><span style={S.k}>Print size</span>{l.width_in && l.height_in ? `${num(l.width_in)}″ W × ${num(l.height_in)}″ H` : '—'}</div>
                    <div><span style={S.k}>Placement</span>{l.placement_text || '—'}</div>
                    <div><span style={S.k}>Print style</span>{l.effective.text}{l.effective.mesh_default ? ` · ${l.effective.mesh_default} mesh` : ''}</div>
                    {l.uses_discharge && <div><span style={S.k}>Discharge</span>{l.discharge_text || 'Discharge screens — see pack settings'}</div>}
                    {l.grading && <div><span style={S.k}>Size grading</span>{l.grading}</div>}
                    {l.placement_notes && <div><span style={S.k}>Placement notes</span>{l.placement_notes}</div>}
                    {l.notes && <div><span style={S.k}>Notes</span>{l.notes}</div>}
                  </div>
                  <div style={{ marginTop: 16, overflowX: 'auto' }}>
                    <span style={S.k}>Inks — print order</span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 520 }}>
                      <thead><tr style={{ textAlign: 'left', color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        <th style={{ padding: '6px 4px' }}>#</th><th /><th>Pantone</th><th>Name</th><th>Mesh</th><th>Underbase</th><th>Flash</th><th>Notes</th></tr></thead>
                      <tbody>
                        {l.colors.map((c, k) => (
                          <tr key={k} style={{ borderTop: `1px solid ${C.line}` }}>
                            <td style={{ padding: '8px 4px' }}>{k + 1}</td><td><Swatch hex={c.hex} size={22} /></td>
                            <td style={{ fontWeight: 700 }}>{c.pantone ? 'PMS ' + c.pantone : '—'}</td><td>{c.name || '—'}</td>
                            <td>{c.mesh || l.effective.mesh_default || '—'}</td><td>{c.underbase ? (c.discharge ? 'Discharge' : 'Yes') : (c.discharge ? 'Discharge ink' : '—')}</td><td>{c.flash ? 'Yes' : '—'}</td><td style={{ color: C.muted }}>{c.notes || ''}</td>
                          </tr>
                        ))}
                        {l.colors.length === 0 && <tr><td colSpan={8} style={{ color: C.muted, padding: 8 }}>No inks listed.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {l.files.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <span style={S.k}>Art files</span>
                      {l.files.map((f) => <div key={f.id}><a href={file(f.id)} style={{ color: C.green, textDecoration: 'none' }}>{f.filename}</a> <span style={{ color: C.muted, fontSize: 12 }}>{fmtBytes(f.bytes)}</span></div>)}
                    </div>
                  )}
                </div>
              );
            })}

            {pack.references.length > 0 && (
              <div style={S.card}>
                <span style={S.k}>Reference files</span>
                {pack.references.map((f) => <div key={f.id}><a href={file(f.id)} style={{ color: C.green, textDecoration: 'none' }}>{f.label || f.filename}</a> <span style={{ color: C.muted, fontSize: 12 }}>{fmtBytes(f.bytes)}</span></div>)}
              </div>
            )}
            <p style={{ color: C.muted, fontSize: 12, textAlign: 'center' }}>Pantone® swatches are on-screen approximations — match inks to a physical Pantone guide. Dimensions in inches. · XYZ Merchandise LLC · xyzmerch.com</p>
          </>
        )}
      </div>
    </div>
  );
}
